import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  WalletIcon,
  LinkIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChevronRightIcon,
  SparklesIcon,
  ShoppingBagIcon,
  TruckIcon,
  ArrowsRightLeftIcon,
  MapPinIcon,
  ArrowUturnLeftIcon,
  BellIcon,
  ArrowPathRoundedSquareIcon,
  PlusIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  ShareIcon,
  BanknotesIcon,
  Squares2X2Icon,
  TagIcon,
  CakeIcon,
  PhotoIcon,
  CubeIcon,
  UserGroupIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useOrderPermissions, usePermissions } from '../../hooks/usePermissionss';
import { useOrderStageAccess } from '../../hooks/useOrderStages';
import apiService from '../../services/api';

// Permission module -> sidebar entry. Shows only when admin granted "view" for that module.
// Every route below exists in App.tsx under /employee.
const EMPLOYEE_MODULE_MENU = [
  { module: 'products',   to: '/employee/products',   label: 'Products',   icon: Squares2X2Icon },
  { module: 'categories', to: '/employee/categories', label: 'Categories', icon: TagIcon },
  { module: 'age_groups', to: '/employee/age-groups', label: 'Age Groups', icon: CakeIcon },
  { module: 'inventory',  to: '/employee/inventory',  label: 'Inventory',  icon: CubeIcon },
  { module: 'slider',     to: '/employee/slider',     label: 'Slider',     icon: PhotoIcon },
  { module: 'employees',  to: '/employee/employees',  label: 'Employees',  icon: UserGroupIcon },
  { module: 'about',      to: '/employee/about',      label: 'About Us',   icon: InformationCircleIcon },
  { module: 'contact',    to: '/employee/contact',    label: 'Contact',    icon: ChatBubbleLeftRightIcon },
  { module: 'settings',   to: '/employee/settings',   label: 'Settings',   icon: Cog6ToothIcon },
];

const getUserInitials = (): string => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'EM';
    const user = JSON.parse(raw);
    const fullName: string =
      user?.name ||
      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
      '';
    if (!fullName) return 'EM';
    return fullName
      .split(' ')
      .filter(Boolean)
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  } catch {
    return 'EM';
  }
};

const getUserName = (): string => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'Employee';
    const user = JSON.parse(raw);
    return (
      user?.name ||
      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
      'Employee'
    );
  } catch {
    return 'Employee';
  }
};

const EmployeeLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get all order permissions including canOrderAgain
  const {
    canView,
    canTrackOrder,
    canManageNDR,
    canProcessReturnExchange,
    canManageRTO,
    canOrderAgain,
    canPlaceOrder,
    loading: orderPermsLoading,
  } = useOrderPermissions();

  // Order Workflow Stages: any granted stage also opens the Orders menu
  const { hasAnyStage } = useOrderStageAccess();

  // Customers module permission (All Customers menu)
  const { permissions: customerPerms } = usePermissions('customers');

  // Warehouse module permission (Warehouse menu)
  const { permissions: warehousePerms } = usePermissions('warehouse');

  // Product Share module permission (Product Share menu)
  const { permissions: productSharePerms } = usePermissions('product_share');

  // COD Configuration / COD Remittance module permissions (menus)
  const { permissions: codConfigPerms } = usePermissions('cod_configuration');
  const { permissions: codRemitPerms } = usePermissions('cod_remittance');

  // Modules whose "view" permission the admin granted (one request for all simple modules)
  const [grantedModules, setGrantedModules] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    apiService
      .getMyPermissions()
      .then((res: any) => {
        if (cancelled || !res?.success) return;
        const list: any[] = res.data?.permissions || [];
        setGrantedModules(list.filter((p) => p?.permissions?.view).map((p) => p.module));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Dynamic menu items - Jo bhi permission hoga woh menu show karega
  const menuItems = useMemo(() => {
    const items = [
      { to: '/employee', icon: HomeIcon, label: 'Dashboard' },
      { to: '/employee/profile', icon: UserCircleIcon, label: 'Profile' },
      { to: '/employee/wallet', icon: WalletIcon, label: 'Wallet' },
      { to: '/employee/referrals', icon: LinkIcon, label: 'Referrals' },
    ];

    // NDR Menu - Sirf tab dikhega jab NDR permission ho
    if (canManageNDR) {
      items.push({ to: '/employee/ndr', icon: BellIcon, label: 'NDR Management' });
    }
    
    // RTO Menu - Sirf tab dikhega jab RTO permission ho
    if (canManageRTO) {
      items.push({ to: '/employee/rto', icon: ArrowUturnLeftIcon, label: 'RTO Management' });
    }
    
    // Return/Exchange Menu
    if (canProcessReturnExchange) {
      items.push({ to: '/employee/returns', icon: ArrowsRightLeftIcon, label: 'Returns & Exchanges' });
    }
    
    // Track Order Menu
    if (canTrackOrder) {
      items.push({ to: '/employee/track-orders', icon: MapPinIcon, label: 'Track Orders' });
    }
    
    // ✅ FIXED: Order Again Menu - Navigate to Employee Order Create page
    if (canOrderAgain) {
      items.push({ 
        to: '/employee/orders/create',  // ✅ Changed from /admin/orders/create to /employee/orders/create
        icon: ArrowPathRoundedSquareIcon, 
        label: 'Order Again' 
      });
    }
    
    // Place Order Menu - Sirf tab dikhega jab admin ne "Place Order" permission di ho
    if (canPlaceOrder) {
      items.push({ to: '/employee/orders/create', icon: PlusIcon, label: 'Place Order' });
    }

    // All Customers Menu - Sirf tab dikhega jab customers view permission ho
    if (customerPerms.view) {
      items.push({ to: '/employee/customers', icon: UsersIcon, label: 'All Customers' });
    }

    // Warehouse Menu - Sirf tab dikhega jab warehouse view permission ho
    if (warehousePerms.view) {
      items.push({ to: '/employee/warehouse', icon: BuildingStorefrontIcon, label: 'Warehouse' });
    }

    // Product Share Menu - Sirf tab dikhega jab product_share view permission ho
    if (productSharePerms.view) {
      items.push({ to: '/employee/product-share', icon: ShareIcon, label: 'Product Share' });
    }

    // COD Remittance Menu - Sirf tab dikhega jab cod_remittance view permission ho
    if (codRemitPerms.view) {
      items.push({ to: '/employee/cod-remittance', icon: BanknotesIcon, label: 'COD Remittance' });
    }

    // COD Configuration Menu - Sirf tab dikhega jab cod_configuration view permission ho
    if (codConfigPerms.view) {
      items.push({ to: '/employee/cod-configuration', icon: BanknotesIcon, label: 'COD Configuration' });
    }

    // Products, Categories, Age Groups, Inventory, Slider, Employees, About, Contact, Settings
    EMPLOYEE_MODULE_MENU.forEach((m) => {
      if (grantedModules.includes(m.module)) {
        items.push({ to: m.to, icon: m.icon, label: m.label });
      }
    });

    // View All Orders Menu (agar view permission ho)
    if (canView || hasAnyStage) {
      items.push({ to: '/employee/orders', icon: ShoppingBagIcon, label: 'All Orders' });
    }

    return items;
  }, [canView, hasAnyStage, canTrackOrder, canManageNDR, canProcessReturnExchange, canManageRTO, canOrderAgain, canPlaceOrder, customerPerms.view, warehousePerms.view, productSharePerms.view, codConfigPerms.view, codRemitPerms.view, grantedModules]);

  const userInitials = getUserInitials();
  const userName = getUserName();

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, isSidebarOpen]);

  const handleMouseEnter = () => {
    if (isMobile) return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsHovering(true);
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setIsCollapsed(true);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const isActive = (path: string, end: boolean = false) => {
    if (end) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/employee') return 'Dashboard';
    if (path.includes('profile')) return 'My Profile';
    if (path.includes('wallet')) return 'Wallet';
    if (path.includes('referrals')) return 'Referrals';
    if (path.includes('ndr')) return 'NDR Management';
    if (path.includes('rto')) return 'RTO Management';
    if (path.includes('returns')) return 'Returns & Exchanges';
    if (path.includes('track-orders')) return 'Track Orders';
    if (path.includes('customers')) return 'All Customers';
    if (path.includes('orders/create')) return 'Create Order'; // ✅ For Order Again page
    if (path.includes('orders')) return 'All Orders';
    return 'Employee Portal';
  };

  const isExpanded = isMobile ? isSidebarOpen : (!isCollapsed || isHovering);

  if (orderPermsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-[#2D6A4F]" />
          <p className="mt-2 text-sm text-gray-500">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Serif+Display:ital@0;1&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .el-root {
          font-family: 'DM Sans', sans-serif;
          background: linear-gradient(135deg, rgba(212,237,218,0.35) 0%, #ffffff 50%, rgba(249,226,231,0.35) 100%);
          min-height: 100vh;
          color: #1f2937;
          display: flex;
        }

        .el-sidebar {
          width: 260px;
          min-width: 260px;
          background: rgba(255,255,255,0.97);
          border-right: 1px solid #D4EDDA;
          box-shadow: 0 20px 25px -5px rgba(45,106,79,0.12);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0; top: 0; bottom: 0;
          z-index: 50;
          transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow-x: hidden;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed {
            width: 72px;
            min-width: 72px;
          }
          .el-sidebar.collapsed .el-brand-text,
          .el-sidebar.collapsed .el-brand-sub,
          .el-sidebar.collapsed .el-nav-link span,
          .el-sidebar.collapsed .el-nav-link-chevron,
          .el-sidebar.collapsed .el-user-name,
          .el-sidebar.collapsed .el-user-status,
          .el-sidebar.collapsed .el-logout-btn span {
            opacity: 0;
            visibility: hidden;
            width: 0;
            display: none;
          }
          .el-sidebar.collapsed .el-nav-link {
            justify-content: center;
            padding: 10px;
          }
          .el-sidebar.collapsed .el-nav-link svg {
            width: 20px;
            height: 20px;
            margin: 0;
          }
          .el-sidebar.collapsed .el-brand-inner {
            justify-content: center;
          }
          .el-sidebar.collapsed .el-brand-icon {
            margin: 0;
          }
          .el-sidebar.collapsed .el-user-row {
            justify-content: center;
            padding: 8px 0;
          }
          .el-sidebar.collapsed .el-user-avatar {
            width: 36px;
            height: 36px;
            font-size: 14px;
          }
          .el-sidebar.collapsed .el-logout-btn {
            justify-content: center;
            padding: 9px;
          }
          .el-sidebar.collapsed .el-logout-btn svg {
            width: 18px;
            height: 18px;
          }
          .el-sidebar.collapsed .el-nav-section {
            text-align: center;
            font-size: 8px;
            letter-spacing: 0;
          }
        }

        @media (max-width: 1023px) {
          .el-sidebar { transform: translateX(-100%); }
          .el-sidebar.open { transform: translateX(0); width: 260px; min-width: 260px; }
        }

        .el-brand {
          position: relative;
          background: linear-gradient(90deg, rgba(212,237,218,0.3), rgba(249,226,231,0.3));
          padding: 26px 20px 20px;
          border-bottom: 1px solid #D4EDDA;
          flex-shrink: 0;
          transition: padding 0.25s ease;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-brand { padding: 26px 16px 20px; }
        }

        .el-brand::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: -1px;
          height: 2px;
          background: linear-gradient(90deg, #2D6A4F, #C5A059, #C27B7F);
        }

        .el-brand-inner {
          display: flex;
          align-items: center;
          gap: 11px;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-brand-inner { gap: 0; }
        }

        .el-brand-icon {
          width: 38px; height: 38px;
          border-radius: 10px;
          background: linear-gradient(135deg, #2D6A4F 0%, #C5A059 100%);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .el-brand-icon svg { width: 17px; height: 17px; color: #fff; }
        .el-brand-text {
          font-family: 'DM Serif Display', serif;
          font-size: 17px;
          background: linear-gradient(90deg, #2D6A4F, #C27B7F);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 700;
          letter-spacing: -0.2px;
          line-height: 1;
          transition: all 0.2s ease;
        }
        .el-brand-sub {
          font-size: 10.5px;
          color: #C5A059;
          letter-spacing: 0.5px;
          margin-top: 2px;
          font-weight: 400;
          transition: all 0.2s ease;
        }
        .el-close-btn {
          display: none;
          width: 30px; height: 30px;
          background: rgba(212,237,218,0.6);
          border: none;
          border-radius: 7px;
          color: #2D6A4F;
          align-items: center; justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          flex-shrink: 0;
          margin-left: auto;
        }
        .el-close-btn:hover { background: #D4EDDA; color: #2D6A4F; }
        .el-close-btn svg { width: 15px; height: 15px; }

        @media (max-width: 1023px) {
          .el-close-btn { display: flex; }
        }

        .el-nav {
          flex: 1;
          padding: 18px 12px;
          overflow-y: auto;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-nav { padding: 18px 8px; }
        }

        .el-nav-section {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          color: #C5A059;
          padding: 12px 10px 6px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .el-nav-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 10px;
          border-radius: 8px;
          color: #374151;
          font-size: 13.5px;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.18s;
          margin-bottom: 1px;
          position: relative;
          overflow: hidden;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-family: 'DM Sans', sans-serif;
          white-space: nowrap;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-nav-link { justify-content: center; }
        }

        .el-nav-link::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 0.18s;
          background: linear-gradient(90deg, #D4EDDA, #F9E2E7);
          border-radius: 8px;
        }
        .el-nav-link > * { position: relative; z-index: 1; }
        .el-nav-link:hover::before { opacity: 1; }
        .el-nav-link:hover { color: #2D6A4F; }

        .el-nav-link.active {
          color: #fff;
          background: linear-gradient(90deg, #2D6A4F 0%, #C27B7F 100%);
          box-shadow: 0 4px 10px rgba(45,106,79,0.25);
          padding-left: 10px;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-nav-link.active {
            border-left: none;
            border-radius: 8px;
            padding: 10px;
          }
        }

        .el-nav-link.active::before { opacity: 0; }
        .el-nav-link svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
          transition: all 0.2s ease;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-nav-link svg { width: 20px; height: 20px; }
        }

        .el-nav-link span { transition: all 0.2s ease; }

        .el-nav-link-chevron {
          width: 14px; height: 14px;
          margin-left: auto;
          opacity: 0.4;
          flex-shrink: 0;
          transition: all 0.2s ease;
        }
        .el-nav-link.active .el-nav-link-chevron { opacity: 1; }

        .el-sidebar-footer {
          padding: 14px 12px;
          border-top: 1px solid #D4EDDA;
          background: linear-gradient(180deg, #ffffff, rgba(249,226,231,0.3));
          flex-shrink: 0;
          transition: padding 0.25s ease;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-sidebar-footer { padding: 14px 8px; }
        }

        .el-user-row {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 8px;
          background: rgba(249,226,231,0.45);
          margin-bottom: 8px;
          transition: all 0.2s ease;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-user-row { justify-content: center; padding: 8px 0; }
        }

        .el-user-avatar {
          width: 30px; height: 30px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2D6A4F, #C27B7F);
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700; color: #fff;
          flex-shrink: 0;
          transition: all 0.2s ease;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-user-avatar { width: 36px; height: 36px; font-size: 14px; }
        }

        .el-user-name {
          font-size: 12.5px;
          font-weight: 600;
          color: #1f2937;
          transition: all 0.2s ease;
        }
        .el-user-status {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 10.5px;
          color: #C27B7F;
          margin-top: 1px;
          transition: all 0.2s ease;
        }
        .el-user-status-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #C5A059;
          flex-shrink: 0;
        }
        .el-logout-btn {
          display: flex;
          align-items: center;
          gap: 9px;
          width: 100%;
          padding: 9px 10px;
          background: none;
          border: 2px solid #D4EDDA;
          border-radius: 8px;
          color: #2D6A4F;
          background-color: #fff;
          font-size: 12.5px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.18s;
          text-align: left;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-logout-btn { justify-content: center; padding: 9px; }
        }

        .el-logout-btn:hover {
          border-color: #C5A059;
          color: #2D6A4F;
          background: linear-gradient(90deg, #D4EDDA, #F9E2E7);
        }
        .el-logout-btn svg { width: 14px; height: 14px; flex-shrink: 0; }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed .el-logout-btn svg { width: 18px; height: 18px; }
        }

        .el-logout-btn span { transition: all 0.2s ease; }

        .el-backdrop {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(45,106,79,0.4);
          z-index: 45;
          backdrop-filter: blur(3px);
        }
        .el-backdrop.visible { display: block; }

        .el-mobile-toggle {
          display: none;
          position: fixed;
          top: 16px; left: 16px;
          z-index: 55;
          width: 40px; height: 40px;
          background: #ffffff;
          border: 1px solid #D4EDDA;
          border-radius: 10px;
          color: #2D6A4F;
          align-items: center; justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(0,0,0,0.25);
          transition: all 0.18s;
        }
        .el-mobile-toggle:hover { background: #D4EDDA; }
        .el-mobile-toggle.sidebar-open { opacity: 0; pointer-events: none; }
        .el-mobile-toggle svg { width: 18px; height: 18px; }

        @media (max-width: 1023px) {
          .el-mobile-toggle { display: flex; }
        }

        .el-main {
          flex: 1;
          margin-left: 260px;
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          width: calc(100% - 260px);
          transition: margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          background: transparent;
        }

        @media (min-width: 1024px) {
          .el-sidebar.collapsed ~ .el-main {
            margin-left: 72px;
            width: calc(100% - 72px);
          }
        }

        @media (max-width: 1023px) {
          .el-main { 
            margin-left: 0; 
            width: 100%;
          }
        }

        .el-topbar {
          height: 60px;
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(8px);
          border-bottom: 1px solid #D4EDDA;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 28px;
          position: sticky;
          top: 0;
          z-index: 30;
          flex-shrink: 0;
        }

        @media (max-width: 1023px) {
          .el-topbar { padding: 0 16px 0 60px; }
        }

        .el-topbar-left { display: flex; align-items: center; gap: 14px; }

        .el-breadcrumb { display: flex; align-items: center; gap: 6px; }
        .el-breadcrumb-home { font-size: 12px; color: #9CA3AF; font-weight: 500; }
        .el-breadcrumb-sep { width: 12px; height: 12px; color: #C8D0CB; }
        .el-breadcrumb-page { font-size: 14px; font-weight: 600; color: #2D6A4F; }

        .el-topbar-right { display: flex; align-items: center; gap: 10px; }

        .el-icon-btn {
          width: 36px; height: 36px;
          border: 1px solid #D4EDDA;
          border-radius: 9px;
          background: transparent;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: #2D6A4F;
          transition: all 0.15s;
        }
        .el-icon-btn:hover { background: #D4EDDA; color: #2D6A4F; border-color: #C5A059; }
        .el-icon-btn svg { width: 17px; height: 17px; }

        .el-topbar-avatar {
          width: 32px; height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2D6A4F, #C27B7F);
          display: flex; align-items: center; justify-content: center;
          font-size: 11.5px; font-weight: 700; color: #fff;
          cursor: pointer;
          border: 2px solid #D4EDDA;
          transition: border-color 0.15s;
          user-select: none;
        }
        .el-topbar-avatar:hover { border-color: #C5A059; }

        .el-content {
          flex: 1;
          padding: 28px 32px;
          width: 100%;
          max-width: 100%;
          background: transparent;
        }

        @media (max-width: 768px) {
          .el-content { 
            padding: 20px 16px; 
          }
        }

        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #D4EDDA; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #C5A059; }
      `}</style>

      <div className="el-root">
        <button
          className={`el-mobile-toggle ${isSidebarOpen ? 'sidebar-open' : ''}`}
          onClick={() => setIsSidebarOpen(true)}
          aria-label="Open menu"
        >
          <Bars3Icon />
        </button>

        <div
          className={`el-backdrop ${isSidebarOpen ? 'visible' : ''}`}
          onClick={() => setIsSidebarOpen(false)}
        />

        <aside
          className={`el-sidebar ${isExpanded ? 'expanded' : 'collapsed'} ${isSidebarOpen ? 'open' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="el-brand">
            <div className="el-brand-inner">
              <div className="el-brand-icon">
                <SparklesIcon />
              </div>
              <div>
                <div className="el-brand-text">Workspace</div>
                <div className="el-brand-sub">Employee Portal</div>
              </div>
              <button className="el-close-btn" onClick={() => setIsSidebarOpen(false)}>
                <XMarkIcon />
              </button>
            </div>
          </div>

          <nav className="el-nav">
            <div className="el-nav-section">Main</div>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`el-nav-link ${active ? 'active' : ''}`}
                  onClick={() => isMobile && setIsSidebarOpen(false)}
                >
                  <Icon />
                  <span>{item.label}</span>
                  {active && <ChevronRightIcon className="el-nav-link-chevron" />}
                </Link>
              );
            })}
          </nav>

          <div className="el-sidebar-footer">
            <div className="el-user-row">
              <div className="el-user-avatar">{userInitials}</div>
              <div>
                <div className="el-user-name">{userName}</div>
                <div className="el-user-status">
                  <div className="el-user-status-dot" />
                  Active
                </div>
              </div>
            </div>
            <button className="el-logout-btn" onClick={handleLogout}>
              <ArrowRightOnRectangleIcon />
              <span>Sign out</span>
            </button>
          </div>
        </aside>

        <div className="el-main">
          <header className="el-topbar">
            <div className="el-topbar-left">
              <div className="el-breadcrumb">
                <span className="el-breadcrumb-home">Portal</span>
                <ChevronRightIcon className="el-breadcrumb-sep" />
                <span className="el-breadcrumb-page">{getPageTitle()}</span>
              </div>
            </div>
            <div className="el-topbar-right">
              <div className="el-topbar-avatar" title={userName}>
                {userInitials}
              </div>
            </div>
          </header>

          <main className="el-content">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
};

export default EmployeeLayout;